import { RefObject, useEffect, useState } from 'react';
import type { InteractionContext } from '~/engine/tool/InteractionContext';
import type { ViewManager } from '~/engine/view/ViewManager';
import { POINTER_SHIELD_ATTRIBUTE } from '~/viewportGrid/chrome';
import { armPan } from './armPan';

/**
 * Routes the grid root's DOM events into the production
 * ViewManager -> ToolManager pipeline. Grid tile canvases are already
 * registered engine views (hit-tested and repainted); this hook attaches the
 * grid root as the input surface of the viewer's interaction context.
 *
 * Returns the viewer's interaction context while the root is attached, and
 * undefined before and after, so a consumer of the context's events hears
 * them only while the grid receives input.
 */
export function useGridEngineInput(
    viewManager: ViewManager | null,
    gridRootRef: RefObject<HTMLElement | null>
): InteractionContext | undefined {
    const [attachedContext, setAttachedContext] = useState<
        InteractionContext | undefined
    >(undefined);
    useEffect(() => {
        const root = gridRootRef.current;
        if (!viewManager || !root) {
            return undefined;
        }
        // Pan is armed before the listeners attach, so no grid event reaches
        // a context with no tool. A tool already armed is the user's choice
        // and is kept.
        const context = viewManager.interactionContext;
        if (!context.toolManager.getActiveTool()) {
            armPan(context.toolManager);
        }
        viewManager.clearActiveView();
        // A pointer resting on a shield is off the image: the tool is put
        // away as when the pointer leaves the grid, and the move stops here.
        // A drag passing over a shield stays its gesture's.
        const shieldWatch = new AbortController();
        let shielded = false;
        root.addEventListener(
            'mousemove',
            (event) => {
                const onShield =
                    event.buttons === 0 &&
                    event.target instanceof Element &&
                    event.target.closest(`[${POINTER_SHIELD_ATTRIBUTE}]`) !==
                        null;
                if (onShield !== shielded) {
                    shielded = onShield;
                    if (onShield) {
                        context.toolManager.onMouseLeave();
                    } else {
                        context.toolManager.onMouseEnter();
                    }
                }
                if (onShield) {
                    event.stopPropagation();
                }
            },
            { capture: true, signal: shieldWatch.signal }
        );
        viewManager.attachInputSurface(root);
        setAttachedContext(context);
        return () => {
            shieldWatch.abort();
            setAttachedContext(undefined);
            viewManager.detachInputSurface(root);
            viewManager.clearActiveView();
            context.dispose();
        };
    }, [viewManager, gridRootRef]);
    return attachedContext;
}
